import { Camelized, camelizeKeys, Decamelized, decamelizeKeys } from 'humps'
import * as React from 'react'
import { css, RuleSet } from 'styled-components'

import { YesNo } from '../models'

const hexRegex = /^#(?:[0-9a-fA-F]{3}){1,2}$/

export function isHex(value: any) {
  return typeof value === 'string' && hexRegex.test(value)
}

export function hexWithOpacity(hex: any, alpha: number) {
  const isAlphaValid = typeof alpha === 'number' && alpha >= 0 && alpha <= 1
  const opacity = isAlphaValid ? alpha : 1

  if (!isHex(hex)) return 'rgb(0,0,0,1)'

  const r = `0x${hex[1]}${hex[2]}`
  const g = `0x${hex[3]}${hex[4]}`
  const b = `0x${hex[5]}${hex[6]}`

  return `rgb(${+r},${+g},${+b},${opacity})`
}

export const smoothScrollDialogIntoView = (fieldName: string, parentSelector?: string) => {
  const fieldElement = document.getElementsByName(fieldName)[0]
  if (!fieldElement) return
  if (parentSelector && fieldElement.closest && fieldElement.closest(parentSelector)) {
    return fieldElement.closest(parentSelector)?.scrollIntoView({ behavior: 'smooth' })
  }

  fieldElement.scrollIntoView({ behavior: 'smooth' })
}

export const smoothScrollIntoView = (fieldName: string, behavior?: ScrollBehavior) => {
  const fieldElement = document.getElementsByName(fieldName)[0] || document.getElementById(fieldName)
  if (!fieldElement) return
  return smoothScrollElementIntoView(fieldElement, undefined, behavior)
}

export const smoothScrollElementIntoView = (
  element: HTMLElement | null,
  offset = 40,
  behavior?: ScrollBehavior
) => {
  if (!element) return
  const bodyRect = document.body.getBoundingClientRect().top
  const elementRect = element.getBoundingClientRect().top
  const elementPosition = elementRect - bodyRect
  const offsetPosition = elementPosition - offset

  window.scrollTo({
    top: offsetPosition,
    behavior: behavior || 'smooth',
  })
}

export function mergeRefs<T = any>(
  refs: Array<React.MutableRefObject<T> | React.LegacyRef<T>>
): React.RefCallback<T> {
  return (value) => {
    refs.forEach((ref) => {
      if (typeof ref === 'function') {
        ref(value)
      } else if (ref != null) {
        ;(ref as React.MutableRefObject<T | null>).current = value
      }
    })
  }
}

export const parseQueryParams = (url: string) => {
  return Object.fromEntries(new URLSearchParams(url.split('?')[1]).entries()) || {}
}

export const fixFloatingPointArithmetic = (x: number, tailingPrecision: number = 2): number => {
  return parseFloat(x.toPrecision(Math.floor(x).toPrecision().length + tailingPrecision))
}

export function isValidDate(d: any) {
  return d instanceof Date && !isNaN(d as any)
}

export const toCamelCase = <T>(o: T): Camelized<T> => {
  return camelizeKeys(o, (key, convert, options) => {
    return isUpperCase(key.replaceAll ? key.replaceAll('_', '') : key) ? key : convert(key, options)
  })
}

export const toSnakeCase = <T>(o: T): Decamelized<T> => {
  return decamelizeKeys(o, (key, convert, options) => {
    return isUpperCase(key.replaceAll ? key.replaceAll('_', '') : key) ? key : convert(key, options)
  })
}

function isUpperCase(str: string) {
  return str === str.toUpperCase() && str !== str.toLowerCase()
}

export const toTitleCase = (str: string) => {
  return str.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase())
}

export const toSentenceCase = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

export const toAllSentenceCase = (str: string) => {
  return str
    .replace(/\w\S*/g, (txt) => txt.toLowerCase())
    .replace(/\w\S*/, (txt) => txt.charAt(0).toUpperCase() + txt.substring(1))
}

export const fieldToTitleCase = (str: string) => {
  return toTitleCase(toSnakeCase(str).replaceAll('_', ' '))
}

export function containerQuery(query: string, styles: string | RuleSet<object>) {
  return css`
    /* Fallback */
    @media ${query} {
      ${styles}
    }

    /* stylelint-disable */
    @container ${query.replace('only screen and', '')} {
      ${styles}
    }
    /* stylelint-enable */
  `
}

// pluralize(0, 'turtle'); // 0 turtles
// pluralize(1, 'turtle'); // 1 turtle
// pluralize(2, 'turtle'); // 2 turtles
// pluralize(3, 'fox', 'es'); // 3 foxes
// pluralize(2, 'discrepancy'); // 2 discrepancies

export const pluralize = (count: number, noun: string, suffix = 's', includeCountPrefix = true) => {
  let pluralSuffix = suffix
  if (count !== 1 && noun.match(/[^aeiou]y$/)) {
    noun = noun.slice(0, -1)
    pluralSuffix = 'ies'
  }
  const pluralPrefix = includeCountPrefix ? `${count} ` : ''
  return `${pluralPrefix}${noun}${count !== 1 ? pluralSuffix : ''}`
}

export const YesNoRadioOptions = [
  {
    label: 'Yes',
    value: YesNo.YES,
  },
  {
    label: 'No',
    value: YesNo.NO,
  },
]

export function convertBooleanToYesNo(val: boolean | null | undefined) {
  if (val === undefined || val === null) return null
  return val ? YesNo.YES : YesNo.NO
}

/**
 * Maps MIME types to appropriate file extensions
 * @param mimeType - The MIME type string
 * @returns The appropriate file extension with leading dot
 */
export function getFileExtension(mimeType: string): string {
  const mimeToExt: Record<string, string> = {
    // PDFs
    'application/pdf': '.pdf',

    // Images
    'image/jpeg': '.jpg',
    'image/jpg': '.jpg',
    'image/png': '.png',
    'image/gif': '.gif',
    'image/webp': '.webp',
    'image/svg+xml': '.svg',
    'image/bmp': '.bmp',
    'image/tiff': '.tiff',

    // Text files
    'text/plain': '.txt',
    'text/html': '.html',
    'text/css': '.css',
    'text/javascript': '.js',
    'text/csv': '.csv',
    'text/xml': '.xml',

    // Documents
    'application/json': '.json',
    'application/xml': '.xml',
    'application/msword': '.doc',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
    'application/vnd.ms-excel': '.xls',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
    'application/vnd.ms-powerpoint': '.ppt',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': '.pptx',

    // Archives
    'application/zip': '.zip',
    'application/x-rar-compressed': '.rar',
    'application/x-7z-compressed': '.7z',
    'application/gzip': '.gz',
    'application/x-tar': '.tar',

    // Audio
    'audio/mpeg': '.mp3',
    'audio/wav': '.wav',
    'audio/ogg': '.ogg',
    'audio/mp4': '.m4a',

    // Video
    'video/mp4': '.mp4',
    'video/webm': '.webm',
    'video/ogg': '.ogv',
    'video/avi': '.avi',
    'video/mov': '.mov',
  }

  return mimeToExt[mimeType] || '.bin' // Default to .bin for unknown types
}

/**
 * Detects the content type of binary data by analyzing its byte signatures
 * @param arrayBuffer - The binary data to analyze
 * @returns The detected MIME type string
 */
export function detectBinaryContentType(arrayBuffer: ArrayBuffer): string {
  const bytes = new Uint8Array(arrayBuffer)

  // PDF detection: starts with %PDF
  if (
    bytes.length > 4 &&
    bytes[0] === 0x25 && // %
    bytes[1] === 0x50 && // P
    bytes[2] === 0x44 && // D
    bytes[3] === 0x46
  ) {
    // F
    return 'application/pdf'
  }

  // JPEG detection: starts with FF D8 FF
  if (bytes.length > 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
    return 'image/jpeg'
  }

  // PNG detection: starts with 89 50 4E 47 0D 0A 1A 0A
  if (
    bytes.length > 8 &&
    bytes[0] === 0x89 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x4e &&
    bytes[3] === 0x47 &&
    bytes[4] === 0x0d &&
    bytes[5] === 0x0a &&
    bytes[6] === 0x1a &&
    bytes[7] === 0x0a
  ) {
    return 'image/png'
  }

  // GIF detection: starts with 47 49 46 38 (GIF8)
  if (bytes.length > 4 && bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x38) {
    return 'image/gif'
  }

  // WebP detection: starts with RIFF followed by WebP
  if (
    bytes.length > 12 &&
    bytes[0] === 0x52 && // R
    bytes[1] === 0x49 && // I
    bytes[2] === 0x46 && // F
    bytes[3] === 0x46 && // F
    bytes[8] === 0x57 && // W
    bytes[9] === 0x45 && // E
    bytes[10] === 0x42 && // B
    bytes[11] === 0x50
  ) {
    // P
    return 'image/webp'
  }

  // Default to generic binary type
  return 'application/octet-stream'
}

/**
 * Detects the content type of a string by analyzing its content
 * @param content - The string content to analyze
 * @returns The detected MIME type string
 */
export function detectContentType(content: string): string {
  // Image detection from data URLs
  if (content.startsWith('data:image/')) {
    const match = content.match(/^data:([^;]+)/)
    return match ? match[1] : 'text/plain'
  }

  // Image detection from base64 strings (common image formats)
  if (content.length > 100 && /^[A-Za-z0-9+/=]+$/.test(content)) {
    // Try to detect image type from base64 content
    try {
      // Check for common image signatures in base64
      const decoded = atob(content)
      const bytes = new Uint8Array(decoded.length)
      for (let i = 0; i < decoded.length; i++) {
        bytes[i] = decoded.charCodeAt(i)
      }

      // Use the binary detection function for consistent logic
      const detectedType = detectBinaryContentType(bytes.buffer)
      if (detectedType !== 'application/octet-stream') {
        return detectedType
      }

      // SVG: contains SVG content
      if (content.includes('<svg') || content.includes('<?xml')) {
        return 'image/svg+xml'
      }

      // If it looks like binary data but we can't identify the type
      return 'application/octet-stream'
    } catch (error) {
      // If base64 decoding fails, fall back to generic binary
      return 'application/octet-stream'
    }
  }

  // PDF detection
  if (content.startsWith('%PDF')) {
    return 'application/pdf'
  }

  // HTML detection
  if (content.startsWith('<html') || content.startsWith('<!DOCTYPE')) {
    return 'text/html'
  }

  // JSON detection
  if (content.startsWith('{') || content.startsWith('[')) {
    return 'application/json'
  }

  // XML detection
  if (content.startsWith('<?xml') || content.startsWith('<')) {
    return 'application/xml'
  }

  // CSV detection
  if (content.includes('\n') && (content.includes(':') || content.includes(','))) {
    return 'text/csv'
  }

  // Data URLs (non-image)
  if (content.startsWith('data:')) {
    const match = content.match(/^data:([^;]+)/)
    return match ? match[1] : 'text/plain'
  }

  // URLs
  if (content.startsWith('http://') || content.startsWith('https://')) {
    return 'text/plain'
  }

  // Default to text/plain for other string content
  return 'text/plain'
}
